import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";
import {
  FaPlus,
  FaTimes,
  FaBold,
  FaItalic,
  FaListUl,
  FaListOl,
} from "react-icons/fa";

// Simple Rich Text Editor component using contentEditable (NO external libraries)
const SimpleRichEditor = ({ initialValue = "", onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  // Add state to track active formats
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    bulletList: false,
    numberedList: false
  });

  // Initialize editor content when component mounts or initialValue changes
  useEffect(() => {
    if (editorRef.current) {
      // Only update if the content is different to avoid selection issues
      if (editorRef.current.innerHTML !== initialValue) {
        editorRef.current.innerHTML = initialValue || "<p><br></p>";
      }
    }
  }, [initialValue]);
  
  // Ensure we have a proper paragraph structure on mount
  useEffect(() => {
    if (editorRef.current) {
      if (!editorRef.current.innerHTML || editorRef.current.innerHTML === "<br>") {
        editorRef.current.innerHTML = "<p><br></p>";
      }
      
      // Make sure there's a paragraph inside for proper editing
      if (!editorRef.current.querySelector('p') && !editorRef.current.querySelector('div') &&
          !editorRef.current.querySelector('ul') && !editorRef.current.querySelector('ol')) {
        const content = editorRef.current.innerHTML;
        editorRef.current.innerHTML = `<p>${content}</p>`;
      }
    }
  }, []);

  // Handle changes to the editor content
  const handleInput = () => {
    if (onChange && editorRef.current) {
      const content = editorRef.current.innerHTML;
      // Only trigger onChange if content actually changed
      if (content !== initialValue) {
        onChange(content);
      }
    }
  };
  
  // Check active formatting on selection change
  const checkActiveFormats = () => {
    // Don't check if the editor doesn't have focus
    if (!isFocused || !document.activeElement || !editorRef.current.contains(document.activeElement)) {
      return;
    }
    
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      bulletList: document.queryCommandState('insertUnorderedList'),
      numberedList: document.queryCommandState('insertOrderedList')
    });
  };
  
  // Monitor selection changes to update active format indicators
  useEffect(() => {
    const handleSelectionChange = () => {
      checkActiveFormats();
    };
    
    // Update active formats when selection changes
    document.addEventListener('selectionchange', handleSelectionChange);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isFocused]);
  
  // Apply formatting when toolbar button is clicked
  const formatText = (command) => {
    // Focus the editor to ensure we can apply formatting
    editorRef.current.focus();
    
    // Apply the appropriate formatting command
    if (command === 'bold' || command === 'italic') {
      document.execCommand(command, false, null);
    } else if (command === 'bulletList') {
      document.execCommand('insertUnorderedList', false, null);
    } else if (command === 'numberedList') {
      document.execCommand('insertOrderedList', false, null);
    } else if (command === 'clear') {
      document.execCommand('removeFormat', false, null);
      
      // Convert lists to paragraphs
      const lists = editorRef.current.querySelectorAll('ul, ol');
      if (lists.length > 0) {
        lists.forEach(list => {
          // Convert each list item to a paragraph
          const items = Array.from(list.querySelectorAll('li'));
          const fragment = document.createDocumentFragment();
          
          items.forEach(item => {
            const p = document.createElement('p');
            p.innerHTML = item.innerHTML || '<br>';
            fragment.appendChild(p);
          });
          
          if (list.parentNode) {
            list.parentNode.replaceChild(fragment, list);
          }
        });
      }
    }
    
    // Update active formats after applying the command
    setTimeout(() => {
      checkActiveFormats();
      handleInput();
    }, 10);
  };
  
  // For keyboard handling
  const handleKeyDown = (e) => {
    // Add special handling for Tab within lists
    if (e.key === 'Tab' && isInList()) {
      e.preventDefault();
      document.execCommand(e.shiftKey ? 'outdent' : 'indent', false, null);
      handleInput();
    }
  };
  
  // Helper to check if cursor is in a list
  const isInList = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return false;

    let node = selection.getRangeAt(0).commonAncestorContainer;
    while (node && node !== editorRef.current) {
      if (['UL', 'OL', 'LI'].includes(node.nodeName)) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  };
  
  // Check active formats on focus
  const handleFocus = () => {
    setIsFocused(true);
    // Wait a bit to check active formats after focus is established
    setTimeout(checkActiveFormats, 10);
  };
  
  return (
    <div className="border rounded-md overflow-hidden rich-editor-container">
      {/* Toolbar with active state indicators */}
      <div className="flex p-1 border-b bg-gray-50">
        <button
          type="button"
          className={`p-1 mx-1 rounded ${activeFormats.bold ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'}`}
          title="Bold"
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent losing focus
            formatText('bold');
          }}
        >
          <FaBold />
        </button>
        <button
          type="button"
          className={`p-1 mx-1 rounded ${activeFormats.italic ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'}`}
          title="Italic"
          onMouseDown={(e) => {
            e.preventDefault();
            formatText('italic');
          }}
        >
          <FaItalic />
        </button>
        <button
          type="button"
          className={`p-1 mx-1 rounded ${activeFormats.bulletList ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'}`}
          title="Bullet List"
          onMouseDown={(e) => {
            e.preventDefault();
            formatText('bulletList');
          }}
        >
          <FaListUl />
        </button>
        <button
          type="button"
          className={`p-1 mx-1 rounded ${activeFormats.numberedList ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'}`}
          title="Numbered List"
          onMouseDown={(e) => {
            e.preventDefault();
            formatText('numberedList');
          }}
        >
          <FaListOl />
        </button>
        <button
          type="button"
          className="p-1 mx-1 rounded hover:bg-gray-200 ml-auto"
          title="Clear Formatting"
          onMouseDown={(e) => {
            e.preventDefault();
            formatText('clear');
          }}
        >
          Clear
        </button>
      </div>
      
      {/* Editor with updated event handlers */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="p-3 min-h-[100px] rich-text-editor"
        onInput={() => {
          handleInput();
          checkActiveFormats();
        }}
        onFocus={handleFocus}
        onBlur={() => setIsFocused(false)}
        onKeyUp={checkActiveFormats}
        onMouseUp={checkActiveFormats}
        onKeyDown={handleKeyDown}
        data-placeholder={
          !isFocused && 
          (!editorRef.current || !editorRef.current.textContent.trim()) 
            ? placeholder 
            : ""
        }
        onPaste={(e) => {
          // Handle paste to maintain formatting but avoid unwanted elements
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
          
          // Update active formats after pasting
          setTimeout(checkActiveFormats, 10);
        }}
      />
      
      {/* Styles */}
      <style jsx global>{`
        .rich-editor-container {
          position: relative;
        }
        
        .rich-text-editor {
          outline: none;
          line-height: 1.5;
          font-family: inherit;
        }
        
        .rich-text-editor:empty:before {
          content: attr(data-placeholder);
          color: #aaa;
          pointer-events: none;
          position: absolute;
        }
        
        .rich-text-editor p {
          margin: 0 0 0.5em 0;
        }
        
        .rich-text-editor p:last-child {
          margin-bottom: 0;
        }
        
        .rich-text-editor ul {
          list-style-type: disc !important;
          padding-left: 1.5em !important;
          margin: 0.5em 0 !important;
        }
        
        .rich-text-editor ol {
          list-style-type: decimal !important;
          padding-left: 1.5em !important;
          margin: 0.5em 0 !important;
        }
        
        .rich-text-editor li {
          display: list-item !important;
        }
        
        .rich-text-editor strong, .rich-text-editor b {
          font-weight: bold !important;
        }
        
        .rich-text-editor em, .rich-text-editor i {
          font-style: italic !important;
        }
      `}</style>
    </div>
  );
};

// Extract plain text from HTML for validation purposes
const htmlToPlainText = (html) => {
  if (!html) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || "";
};

// We use forwardRef to allow the parent component (where Swal is called)
// to get a reference to this component and call a function to get the form data.
const CreateTourForm = forwardRef((props, ref) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState("");
  const [maxPersons, setMaxPersons] = useState(8);
  const [tourImage, setTourImage] = useState(null);
  const [vehicleType, setVehicleType] = useState("car");
  const [pricing, setPricing] = useState({
    car: { lowSeason: "", mediumSeason: "", highSeason: "" },
    minivan: { lowSeason: "", mediumSeason: "", highSeason: "" },
    van: { lowSeason: "", mediumSeason: "", highSeason: "" },
    minibus: { lowSeason: "", mediumSeason: "", highSeason: "" },
  });
  const [locations, setLocations] = useState([""]); // Start with one empty location input
  const [highlights, setHighlights] = useState([""]); // Start with one empty highlight input

  // --- Location Handlers ---
  const handleLocationChange = (index, value) => {
    const newLocations = [...locations];
    newLocations[index] = value;
    setLocations(newLocations);
  };

  const addLocation = () => {
    setLocations([...locations, ""]); // Add a new empty string
  };

  const removeLocation = (index) => {
    if (locations.length > 1) {
      // Prevent removing the last input
      const newLocations = locations.filter((_, i) => i !== index);
      setLocations(newLocations);
    } else {
      setLocations([""]); // Reset the last input if trying to remove it
    }
  };

  // --- Highlight Handlers ---
  const handleHighlightChange = (index, value) => {
    const newHighlights = [...highlights];
    newHighlights[index] = value;
    setHighlights(newHighlights);
  };

  const addHighlight = () => {
    setHighlights([...highlights, ""]); // Add a new empty string
  };

  const removeHighlight = (index) => {
    if (highlights.length > 1) {
      // Prevent removing the last input
      const newHighlights = highlights.filter((_, i) => i !== index);
      setHighlights(newHighlights);
    } else {
      setHighlights([""]); // Reset the last input if trying to remove it
    }
  };

  // --- Pricing Handlers ---
  const handlePricingChange = (vehicle, season, value) => {
    setPricing((prevPricing) => ({
      ...prevPricing,
      [vehicle]: {
        ...prevPricing[vehicle],
        [season]: value === "" ? "" : Number(value), // Store as number or empty string
      },
    }));
  };

  // --- Image Handler ---
  const handleImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setTourImage(event.target.files[0]);
    } else {
      setTourImage(null);
    }
  };

  // --- Expose function to parent via ref ---
  useImperativeHandle(ref, () => ({
    getFormData: () => {
      // --- Basic Validation Example ---
      if (!name || !description || !duration || !maxPersons) {
        console.error("Missing basic fields");
        return null; // Indicate validation failure
      }

      // Check if description has content
      if (!htmlToPlainText(description).trim()) {
        console.error("Description cannot be empty");
        return null;
      }

      // Check if all pricing fields are filled (or handle optional pricing)
      for (const vehicle in pricing) {
        for (const season in pricing[vehicle]) {
          if (pricing[vehicle][season] === "" || pricing[vehicle][season] < 0) {
            console.error(
              `Missing or invalid price for ${vehicle} - ${season}`
            );
            return null; // Indicate validation failure
          }
        }
      }

      // Filter out empty locations and highlights
      const finalLocations = locations.filter(
        (loc) => htmlToPlainText(loc).trim() !== ""
      );
      const finalHighlights = highlights.filter(
        (h) => htmlToPlainText(h).trim() !== ""
      );

      if (finalLocations.length === 0) {
        console.error("At least one location is required");
        return null;
      }

      if (finalHighlights.length === 0) {
        console.error("At least one highlight is required");
        return null;
      }

      // Return collected data
      return {
        name,
        description,
        duration: Number(duration),
        maxPersons: Number(maxPersons),
        tourImage, // The file object itself
        vehicleType,
        pricing, // The nested pricing object
        locations: finalLocations, // Filtered array
        highlights: finalHighlights, // Filtered array
        isActive: true, // Assuming default
      };
    },
  }));

  // --- Render Form ---
  return (
    <form id="createTourFormReact" className="space-y-6 text-left">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      {/* Description with Simple Rich Editor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <SimpleRichEditor
          initialValue={description}
          onChange={setDescription}
          placeholder="Enter tour description..."
        />
      </div>

      {/* Duration & Max Persons */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Duration (hours)
          </label>
          <input
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            type="number"
            min="1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Max Persons
          </label>
          <input
            value={maxPersons}
            onChange={(e) => setMaxPersons(e.target.value)}
            type="number"
            min="1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
      </div>

      {/* Tour Image */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Tour Image
        </label>
        <input
          id="tourImage"
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        <p className="text-xs text-gray-500 mt-1">
          Upload an image for the tour (Max Size: 5MB)
        </p>
      </div>

      {/* Vehicle Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Default Vehicle Type
        </label>
        <select
          value={vehicleType}
          onChange={(e) => setVehicleType(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="car">Car</option>
          <option value="minivan">Mini Van</option>
          <option value="van">Van</option>
          <option value="minibus">Mini Bus</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Select the default type of vehicle for the tour.
        </p>
      </div>

      {/* --- Pricing Section --- */}
      <div className="mt-4 border-t pt-4">
        <h3 className="text-md font-bold text-gray-800 mb-3">
          Pricing by Vehicle Type (€)
        </h3>
        {Object.entries(pricing).map(([vehicleKey, seasons]) => (
          <div
            key={vehicleKey}
            className={`p-3 mb-4 ${
              vehicleKey === "car"
                ? "bg-blue-50 border-blue-100"
                : vehicleKey === "minivan"
                ? "bg-green-50 border-green-100"
                : vehicleKey === "van"
                ? "bg-purple-50 border-purple-100"
                : "bg-amber-50 border-amber-100" // minibus
            } rounded-lg border`}
          >
            <h4
              className={`text-sm font-semibold mb-2 ${
                vehicleKey === "car"
                  ? "text-blue-700"
                  : vehicleKey === "minivan"
                  ? "text-green-700"
                  : vehicleKey === "van"
                  ? "text-purple-700"
                  : "text-amber-700" // minibus
              }`}
            >
              {vehicleKey.charAt(0).toUpperCase() + vehicleKey.slice(1)} Pricing
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(seasons).map(([seasonKey, value]) => (
                <div key={seasonKey}>
                  <label className="block text-xs font-medium text-gray-700">
                    {seasonKey
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={value}
                    onChange={(e) =>
                      handlePricingChange(vehicleKey, seasonKey, e.target.value)
                    }
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-${
                      vehicleKey === "car"
                        ? "blue"
                        : vehicleKey === "minivan"
                        ? "green"
                        : vehicleKey === "van"
                        ? "purple"
                        : "amber" // minibus
                    }-500 focus:ring-${
                      vehicleKey === "car"
                        ? "blue"
                        : vehicleKey === "minivan"
                        ? "green"
                        : vehicleKey === "van"
                        ? "purple"
                        : "amber" // minibus
                    }-500 text-sm`}
                    required
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* --- Locations with Simple Rich Editor --- */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Locations
        </label>
        {locations.map((location, index) => (
          <div key={index} className="flex items-start space-x-2 mb-4">
            <div className="flex-grow min-w-0">
              <SimpleRichEditor
                initialValue={location}
                onChange={(content) => handleLocationChange(index, content)}
                placeholder={`Location ${index + 1}`}
              />
            </div>
            <button
              type="button"
              onClick={() => removeLocation(index)}
              className={`p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100 ${
                locations.length <= 1 ? "opacity-50 cursor-not-allowed" : ""
              } mt-2`}
              disabled={locations.length <= 1}
              aria-label="Remove Location"
            >
              <FaTimes size="1em" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addLocation}
          className="mt-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center"
        >
          <FaPlus className="mr-1" size="0.8em" /> Add Location
        </button>
      </div>

      {/* --- Highlights with Simple Rich Editor --- */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Highlights
        </label>
        {highlights.map((highlight, index) => (
          <div key={index} className="flex items-start space-x-2 mb-4">
            <div className="flex-grow min-w-0">
              <SimpleRichEditor
                initialValue={highlight}
                onChange={(content) => handleHighlightChange(index, content)}
                placeholder={`Highlight ${index + 1}`}
              />
            </div>
            <button
              type="button"
              onClick={() => removeHighlight(index)}
              className={`p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100 ${
                highlights.length <= 1 ? "opacity-50 cursor-not-allowed" : ""
              } mt-2`}
              disabled={highlights.length <= 1}
              aria-label="Remove Highlight"
            >
              <FaTimes size="1em" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addHighlight}
          className="mt-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center"
        >
          <FaPlus className="mr-1" size="0.8em" /> Add Highlight
        </button>
      </div>
    </form>
  );
});

export default CreateTourForm;
